<script setup>
import { onMounted, computed, ref } from 'vue'
import { useWeather } from '@/Composables/useWeather.js'

const { clima, city, loading, fetchByGeolocation } = useWeather()

onMounted(() => {
    fetchByGeolocation()
})

const mostrarDetalhes = ref(false)

const WEATHER_MAP = {
    0:  { emoji: '☀️',  label: 'Céu limpo',              categoria: 'sunny'   },
    1:  { emoji: '🌤️', label: 'Principalmente limpo',    categoria: 'partial' },
    2:  { emoji: '⛅',  label: 'Parcialmente nublado',    categoria: 'partial' },
    3:  { emoji: '☁️',  label: 'Nublado',                 categoria: 'cloudy'  },
    45: { emoji: '🌫️', label: 'Neblina',                 categoria: 'fog'     },
    48: { emoji: '🌫️', label: 'Neblina com geada',       categoria: 'fog'     },
    51: { emoji: '🌦️', label: 'Garoa leve',              categoria: 'drizzle' },
    53: { emoji: '🌦️', label: 'Garoa moderada',          categoria: 'drizzle' },
    55: { emoji: '🌧️', label: 'Garoa intensa',           categoria: 'drizzle' },
    56: { emoji: '🌧️', label: 'Garoa com gelo',          categoria: 'drizzle' },
    57: { emoji: '🌧️', label: 'Garoa intensa com gelo',  categoria: 'drizzle' },
    61: { emoji: '🌧️', label: 'Chuva leve',              categoria: 'rain'    },
    63: { emoji: '🌧️', label: 'Chuva moderada',          categoria: 'rain'    },
    65: { emoji: '🌧️', label: 'Chuva forte',             categoria: 'rain'    },
    67: { emoji: '🌧️', label: 'Chuva forte com gelo',    categoria: 'rain'    },
    71: { emoji: '🌨️', label: 'Neve leve',               categoria: 'snow'    },
    73: { emoji: '🌨️', label: 'Neve moderada',           categoria: 'snow'    },
    75: { emoji: '🌨️', label: 'Neve intensa',            categoria: 'snow'    },
    77: { emoji: '🌨️', label: 'Granizo',                 categoria: 'snow'    },
    80: { emoji: '🌦️', label: 'Pancadas de chuva',       categoria: 'showers' },
    81: { emoji: '🌦️', label: 'Pancadas moderadas',      categoria: 'showers' },
    82: { emoji: '⛈️',  label: 'Pancadas fortes',         categoria: 'showers' },
    95: { emoji: '⛈️',  label: 'Tempestade',              categoria: 'storm'   },
    96: { emoji: '⛈️',  label: 'Tempestade com granizo',  categoria: 'storm'   },
    99: { emoji: '⛈️',  label: 'Tempestade intensa',      categoria: 'storm'   },
}

const PERIOD_LABELS = {
    sunny:   { manha: 'Manhã ensolarada',        tarde: 'Tarde de sol',                noite: 'Noite de céu limpo'          },
    partial: { manha: 'Sol com nuvens de manhã', tarde: 'Tarde parcialmente nublada',  noite: 'Noite com poucas nuvens'     },
    cloudy:  { manha: 'Manhã nublada',           tarde: 'Tarde nublada',               noite: 'Noite nublada'               },
    fog:     { manha: 'Manhã com neblina',       tarde: 'Tarde com neblina',           noite: 'Noite com neblina'           },
    drizzle: { manha: 'Manhã com garoa',         tarde: 'Tarde com garoa',             noite: 'Noite com garoa'             },
    rain:    { manha: 'Manhã chuvosa',           tarde: 'Tarde com chuva',             noite: 'Noite com chuva'             },
    showers: { manha: 'Manhã com pancadas',      tarde: 'Tarde com pancadas de chuva', noite: 'Noite com pancadas de chuva' },
    storm:   { manha: 'Manhã de tempestade',     tarde: 'Tarde de tempestade',         noite: 'Noite de tempestade'         },
    snow:    { manha: 'Manhã com neve',          tarde: 'Tarde com neve',              noite: 'Noite com neve'              },
}

const weatherData = computed(() => {
    const code = clima.value?.current?.weather_code
    return WEATHER_MAP[code] ?? { emoji: '🌡️', label: 'Condição desconhecida', categoria: 'sunny' }
})

const currentTemp = computed(() => Math.round(clima.value?.current?.temperature_2m ?? 0))
const maxTemp     = computed(() => Math.round(clima.value?.daily?.temperature_2m_max?.[0] ?? 0))
const minTemp     = computed(() => Math.round(clima.value?.daily?.temperature_2m_min?.[0] ?? 0))
const feelsLike   = computed(() => Math.round(clima.value?.current?.apparent_temperature ?? 0))

const formatTime = (isoStr) => {
    if (!isoStr) return '--'
    return new Date(isoStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const sunrise = computed(() => formatTime(clima.value?.daily?.sunrise?.[0]))
const sunset  = computed(() => formatTime(clima.value?.daily?.sunset?.[0]))

function getPeriod(startHour, endHour) {
    const times = clima.value?.hourly?.time ?? []
    const codes = clima.value?.hourly?.weather_code ?? []
    const temps = clima.value?.hourly?.temperature_2m ?? []
    const todayStr = times[0]?.slice(0, 10)
    if (!todayStr) return null

    const indices = times.reduce((acc, t, i) => {
        const h = new Date(t).getHours()
        if (t.startsWith(todayStr) && h >= startHour && h <= endHour) acc.push(i)
        return acc
    }, [])
    if (!indices.length) return null

    const freq = {}
    indices.forEach(i => { freq[codes[i]] = (freq[codes[i]] ?? 0) + 1 })
    const dominantCode = Number(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0])
    const avgTemp = Math.round(indices.reduce((s, i) => s + temps[i], 0) / indices.length)
    return { code: dominantCode, temp: avgTemp }
}

const periodos = computed(() => ({
    manha: getPeriod(6, 11),
    tarde: getPeriod(12, 17),
    noite: getPeriod(18, 23),
}))

function labelPeriodo(code, periodo) {
    const cat = WEATHER_MAP[code]?.categoria ?? 'sunny'
    return PERIOD_LABELS[cat]?.[periodo] ?? WEATHER_MAP[code]?.label ?? ''
}

const descricaoTextual = computed(() => {
    const { manha, tarde, noite } = periodos.value
    const partes = []
    if (manha) partes.push(labelPeriodo(manha.code, 'manha'))
    if (tarde) partes.push(labelPeriodo(tarde.code, 'tarde'))
    if (noite) partes.push(labelPeriodo(noite.code, 'noite'))
    return partes.join('. ') + (partes.length ? '.' : '')
})

const nextDays = computed(() => {
    if (!clima.value?.daily?.time) return []
    return clima.value.daily.time.slice(1, 11).map((dateStr, i) => ({
        weekday:  new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short' }),
        fullDate: new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'numeric' }),
        max:  Math.round(clima.value.daily.temperature_2m_max[i + 1]),
        min:  Math.round(clima.value.daily.temperature_2m_min[i + 1]),
        code: clima.value.daily.weather_code?.[i + 1] ?? 0,
    }))
})

const getDayEmoji = (code) => WEATHER_MAP[code]?.emoji ?? '🌡️'
</script>

<template>
    <div class="w-full pb-2">

        <!-- Barra compacta sempre visível -->
        <div class="flex items-center gap-2 text-sm w-full min-w-0">

            <span
                v-if="loading"
                class="loading loading-spinner loading-xs text-admin-primary"
                aria-label="Carregando"
            />

            <template v-else-if="clima">
                <button
                    @click="mostrarDetalhes = !mostrarDetalhes"
                    class="flex items-center gap-1.5 shrink-0 px-2 py-1 rounded-lg hover:bg-sky-50 transition-colors border border-transparent hover:border-sky-100"
                    :aria-expanded="mostrarDetalhes"
                    :aria-label="mostrarDetalhes ? 'Esconder detalhes do clima' : 'Ver detalhes do clima'"
                >
                    <span class="text-lg leading-none" aria-hidden="true">{{ weatherData.emoji }}</span>
                    <span class="font-bold text-gray-800">{{ currentTemp }}°C</span>
                    <span class="hidden sm:inline text-xs text-gray-400">↑{{ maxTemp }}° ↓{{ minTemp }}°</span>
                    <span class="hidden sm:inline text-gray-300">·</span>
                    <span class="hidden sm:inline text-admin-primary font-semibold truncate max-w-[120px]" :title="city">
                        {{ city }}
                    </span>
                    <svg
                        class="w-3.5 h-3.5 text-gray-400 transition-transform duration-200 shrink-0"
                        :class="{ 'rotate-180': mostrarDetalhes }"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                <span class="sm:hidden text-admin-primary font-semibold truncate min-w-0 text-xs" :title="city">
                    {{ city }}
                </span>
            </template>
        </div>

        <!-- Painel expansível -->
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
        >
            <div v-if="mostrarDetalhes && clima" class="mt-2 rounded-2xl overflow-hidden shadow-lg">

                <!-- 1. Cabeçalho com gradiente -->
                <div class="bg-gradient-to-br from-sky-500 to-blue-700 p-4 sm:p-5 text-white">
                    <div class="flex items-start justify-between gap-4">
                        <div class="min-w-0">
                            <div class="flex items-end gap-1 leading-none">
                                <span class="text-6xl font-extrabold tracking-tight">{{ currentTemp }}°</span>
                                <span class="text-2xl text-sky-200 mb-1 font-light">C</span>
                            </div>
                            <p class="text-sky-100 text-sm font-medium mt-2">{{ weatherData.label }}</p>
                            <p class="text-sky-200 text-xs mt-0.5 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path fill-rule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.079 3.566-4.994 3.566-8.327C20 5.785 16.215 2 11.54 2A8.96 8.96 0 002 11.041c0 3.333 1.622 6.248 3.566 8.327a19.58 19.58 0 002.683 2.282 16.975 16.975 0 001.144.742zM12 13.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" clip-rule="evenodd" />
                                </svg>
                                {{ city }}
                            </p>
                        </div>
                        <span class="text-7xl leading-none shrink-0 select-none" aria-hidden="true">
                            {{ weatherData.emoji }}
                        </span>
                    </div>

                    <!-- Métricas -->
                    <div class="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 pt-4 border-t border-white/20 text-xs text-sky-100">
                        <span>↑ <strong class="text-white">{{ maxTemp }}°</strong> &nbsp;↓ <span class="text-sky-300">{{ minTemp }}°</span></span>
                        <span>Sensação <strong class="text-white">{{ feelsLike }}°C</strong></span>
                        <span>Umidade <strong class="text-white">{{ clima.current.relative_humidity_2m }}%</strong></span>
                        <span>Vento <strong class="text-white">{{ clima.current.wind_speed_10m }} km/h</strong></span>
                        <span>Chuva <strong class="text-white">{{ clima.daily.precipitation_probability_max[0] }}%</strong></span>
                    </div>

                    <!-- Nascer e Pôr do sol -->
                    <div class="flex gap-5 mt-3 pt-3 border-t border-white/20 text-xs text-sky-100">
                        <span class="flex items-center gap-1.5">
                            <span aria-hidden="true">🌅</span>
                            Nascer do sol: <strong class="text-white ml-0.5">{{ sunrise }}</strong>
                        </span>
                        <span class="flex items-center gap-1.5">
                            <span aria-hidden="true">🌇</span>
                            Pôr do sol: <strong class="text-white ml-0.5">{{ sunset }}</strong>
                        </span>
                    </div>
                </div>

                <!-- 2. Descrição textual + períodos do dia -->
                <div class="bg-white px-4 py-3 space-y-3">
                    <p v-if="descricaoTextual" class="text-sm text-gray-600 italic leading-relaxed">
                        {{ descricaoTextual }}
                    </p>

                    <div class="grid grid-cols-3 gap-2 text-center text-xs">
                        <div v-if="periodos.manha" class="bg-amber-50 border border-amber-100 rounded-xl p-2.5">
                            <p class="text-amber-600 font-semibold mb-1">Manhã</p>
                            <span class="text-2xl leading-none" aria-hidden="true">{{ getDayEmoji(periodos.manha.code) }}</span>
                            <p class="font-bold text-gray-700 mt-1">{{ periodos.manha.temp }}°C</p>
                        </div>
                        <div v-if="periodos.tarde" class="bg-orange-50 border border-orange-100 rounded-xl p-2.5">
                            <p class="text-orange-600 font-semibold mb-1">Tarde</p>
                            <span class="text-2xl leading-none" aria-hidden="true">{{ getDayEmoji(periodos.tarde.code) }}</span>
                            <p class="font-bold text-gray-700 mt-1">{{ periodos.tarde.temp }}°C</p>
                        </div>
                        <div v-if="periodos.noite" class="bg-indigo-50 border border-indigo-100 rounded-xl p-2.5">
                            <p class="text-indigo-600 font-semibold mb-1">Noite</p>
                            <span class="text-2xl leading-none" aria-hidden="true">{{ getDayEmoji(periodos.noite.code) }}</span>
                            <p class="font-bold text-gray-700 mt-1">{{ periodos.noite.temp }}°C</p>
                        </div>
                    </div>
                </div>

                <!-- 3. Strip de previsão dos próximos 10 dias -->
                <div v-if="nextDays.length" class="bg-blue-800 px-4 py-3">
                    <p class="text-[10px] font-semibold text-blue-300 uppercase mb-2 tracking-wider">Próximos dias</p>
                    <div class="flex gap-0.5 overflow-x-auto pb-0.5 snap-x scrollbar-thin" role="list" aria-label="Previsão dos próximos dias">
                        <div
                            v-for="(day, i) in nextDays"
                            :key="i"
                            role="listitem"
                            class="flex flex-col items-center min-w-[4.5rem] sm:min-w-[5.5rem] py-2 px-1.5 rounded-xl hover:bg-white/10 transition-colors snap-start shrink-0 text-white"
                        >
                            <p class="text-[11px] font-semibold text-blue-300 capitalize">
                                {{ i === 0 ? 'Amanhã' : day.weekday }}
                            </p>
                            <p class="text-[10px] text-blue-400">{{ day.fullDate }}</p>
                            <span class="text-2xl my-1 leading-none" aria-hidden="true">{{ getDayEmoji(day.code) }}</span>
                            <div class="flex items-center gap-1 text-xs">
                                <span class="font-bold">{{ day.max }}°</span>
                                <span class="text-blue-300">{{ day.min }}°</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 4. Créditos -->
                <div class="bg-gray-50 border-t border-gray-100 px-4 py-1.5 text-right">
                    <a
                        href="https://open-meteo.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        Dados meteorológicos: Open-Meteo.com
                    </a>
                </div>

            </div>
        </Transition>
    </div>
</template>

<style scoped>
.scrollbar-thin::-webkit-scrollbar { height: 3px; }
.scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
.scrollbar-thin::-webkit-scrollbar-thumb { background-color: rgba(255,255,255,0.2); border-radius: 999px; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.2) transparent; }

@media (max-width: 640px) {
    .scrollbar-thin::-webkit-scrollbar { display: none; }
    .scrollbar-thin { -ms-overflow-style: none; scrollbar-width: none; }
}
</style>
